import { useCallback, useEffect, useRef, useState } from "react";
import { Button, message as antdMessage } from "antd";
import {
  AudioOutlined,
  BorderOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import { authController } from "../../AppController";
import { APIRoutes } from "../../config";
import { getApiBaseUrl } from "./apiBase";
import styles from "./styles.module.scss";

/**
 * Voice-to-text control for the Neo Design 3.2 composer (NOT legacy Neo — this
 * lives only in ``pages/Neo/NeoConversationPanel.tsx``, whose legacy twin under
 * ``pages/Neo/legacy/`` is intentionally frozen).
 *
 * Idle:        a circular microphone button, sat immediately left of Send.
 * Recording:   the button becomes a Stop (square) and an opaque overlay takes
 *              over the input row — a cancel (X), a live waveform, and Stop —
 *              mirroring the phone-style recording bar.
 * Transcribing: the waveform switches to an indeterminate shimmer while the
 *              audio is POSTed to ``/neo/transcribe`` and the text is handed
 *              back to the composer via ``onTranscript``.
 *
 * The whole thing is self-contained: mic capture, waveform rendering, and the
 * multipart upload all live here so the composer only needs a callback.
 */

type VoiceStatus = "idle" | "recording" | "transcribing";

/** Pick a MediaRecorder mime the browser actually supports. Chrome/Firefox do
 *  webm/opus; Safari only does mp4. All are in the backend allowlist. */
const pickAudioMimeType = (): string => {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  const Rec = (window as any).MediaRecorder;
  if (Rec && typeof Rec.isTypeSupported === "function") {
    for (const type of candidates) {
      if (Rec.isTypeSupported(type)) return type;
    }
  }
  return "";
};

const filenameForMime = (mime: string): string => {
  if (mime.includes("mp4")) return "recording.mp4";
  if (mime.includes("ogg")) return "recording.ogg";
  return "recording.webm";
};

interface NeoVoiceInputProps {
  /** Insert the transcribed text into the composer. */
  onTranscript: (text: string) => void;
  /** Block starting a new recording (composer disabled / turn in progress). */
  disabled?: boolean;
  /** Current authorized clinic, sent for exact transcription billing. */
  selectedClinicId?: number | null;
  /** Fires when recording/transcribing starts (true) and ends (false). The
   *  composer uses this to disable the controls hidden behind the overlay so
   *  keyboard users can't tab into (and fire) Send/Attach mid-recording. */
  onActiveChange?: (active: boolean) => void;
  /** Override the idle microphone shape for composer-specific UI styling. */
  buttonShape?: "circle" | "default";
  /** Keep recording controls inline instead of covering the whole composer. */
  variant?: "compact" | "overlay";
}

const NeoVoiceInput = ({
  onTranscript,
  disabled = false,
  selectedClinicId,
  onActiveChange,
  buttonShape = "circle",
  variant = "overlay",
}: NeoVoiceInputProps) => {
  const [status, setStatus] = useState<VoiceStatus>("idle");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Set true when the user cancels so the recorder's ``onstop`` skips upload.
  const cancelledRef = useRef(false);
  const statusRef = useRef<VoiceStatus>("idle");
  statusRef.current = status;

  const stopWaveformLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const teardownAudio = useCallback(() => {
    stopWaveformLoop();
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    mediaRecorderRef.current = null;
  }, [stopWaveformLoop]);

  // Cleanup on unmount: never leave the mic hot.
  useEffect(() => teardownAudio, [teardownAudio]);

  // Tell the composer whether the overlay is up so it can disable the controls
  // behind it (Attach / textarea / Send) for keyboard + screen-reader users.
  useEffect(() => {
    onActiveChange?.(status !== "idle");
  }, [status, onActiveChange]);
  useEffect(() => () => onActiveChange?.(false), [onActiveChange]);

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      rafRef.current = requestAnimationFrame(drawWaveform);
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      rafRef.current = requestAnimationFrame(drawWaveform);
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 200;
    const cssHeight = canvas.clientHeight || 34;
    if (canvas.width !== Math.round(cssWidth * dpr)) {
      canvas.width = Math.round(cssWidth * dpr);
    }
    if (canvas.height !== Math.round(cssHeight * dpr)) {
      canvas.height = Math.round(cssHeight * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const barWidth = 3;
    const gap = 3;
    const step = barWidth + gap;
    const barCount = Math.max(1, Math.floor(cssWidth / step));
    const mid = cssHeight / 2;
    ctx.fillStyle = variant === "compact" ? "#737b86" : "#1a1a1a";

    const analyser = analyserRef.current;
    if (statusRef.current === "recording" && analyser) {
      const bins = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(bins);
      for (let i = 0; i < barCount; i++) {
        // Sample across the lower/mid frequency range where voice energy sits.
        const binIndex = Math.floor((i / barCount) * (bins.length * 0.6));
        const amplitude = bins[binIndex] / 255; // 0..1
        const barHeight = Math.max(2, amplitude * (cssHeight - 4));
        const x = i * step;
        ctx.fillRect(x, mid - barHeight / 2, barWidth, barHeight);
      }
    } else {
      // Transcribing (or waiting for the analyser): indeterminate shimmer.
      const now = performance.now() / 1000;
      for (let i = 0; i < barCount; i++) {
        const wave = Math.sin(now * 6 + i * 0.5) * 0.5 + 0.5; // 0..1
        const barHeight = Math.max(2, (0.25 + wave * 0.5) * (cssHeight - 4));
        const x = i * step;
        ctx.globalAlpha = 0.35 + wave * 0.4;
        ctx.fillRect(x, mid - barHeight / 2, barWidth, barHeight);
      }
      ctx.globalAlpha = 1;
    }

    rafRef.current = requestAnimationFrame(drawWaveform);
  }, [variant]);

  const transcribe = useCallback(
    async (blob: Blob) => {
      const url = getApiBaseUrl() + APIRoutes.apiNeoTranscribe;
      let token = "";
      try {
        token = (await authController.getAuthenticationToken()) || "";
      } catch {
        /* fall through; the request will 401 */
      }

      const mime = blob.type || "audio/webm";
      const formData = new FormData();
      formData.append("audio_file", blob, filenameForMime(mime));
      // Pin transcription to English (ISO-639-1) rather than letting OpenAI
      // auto-detect; our clinicians dictate in English, so the hint improves
      // accuracy and avoids occasional misdetection on short clips.
      formData.append("language", "en");
      if (selectedClinicId != null) {
        formData.append("clinic_id", String(selectedClinicId));
      }

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          // Do NOT set Content-Type — the browser adds the multipart boundary.
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          const j = (await resp.json()) as { detail?: string };
          if (j && typeof j.detail === "string") detail = j.detail;
        } catch {
          /* keep generic */
        }
        throw new Error(detail);
      }
      const data = (await resp.json()) as { text?: string };
      return (data.text || "").trim();
    },
    [selectedClinicId]
  );

  const start = useCallback(async () => {
    if (disabled || statusRef.current !== "idle") return;

    if (
      !navigator.mediaDevices?.getUserMedia ||
      !(window as any).MediaRecorder
    ) {
      antdMessage.error("Voice input isn't supported in this browser.");
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      antdMessage.error("Microphone access was blocked.");
      return;
    }

    cancelledRef.current = false;
    chunksRef.current = [];
    mediaStreamRef.current = stream;

    // Live waveform: feed the mic stream through an analyser node.
    try {
      const AudioCtx =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioContext: AudioContext = new AudioCtx();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
    } catch {
      // Waveform is best-effort; recording still works without it.
      analyserRef.current = null;
    }

    const mimeType = pickAudioMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      antdMessage.error("Couldn't start recording.");
      teardownAudio();
      return;
    }
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const type = mimeType || chunksRef.current[0]?.type || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      teardownAudio();

      if (cancelledRef.current || blob.size === 0) {
        setStatus("idle");
        return;
      }

      setStatus("transcribing");
      transcribe(blob)
        .then((text) => {
          if (text) onTranscript(text);
          else antdMessage.info("No speech detected.");
        })
        .catch((err: unknown) => {
          const msg =
            err instanceof Error ? err.message : "Transcription failed.";
          antdMessage.error(msg);
        })
        .finally(() => setStatus("idle"));
    };

    recorder.start();
    setStatus("recording");
    stopWaveformLoop();
    rafRef.current = requestAnimationFrame(drawWaveform);
  }, [
    disabled,
    drawWaveform,
    onTranscript,
    stopWaveformLoop,
    teardownAudio,
    transcribe,
  ]);

  const stop = useCallback(() => {
    if (statusRef.current !== "recording") return;
    stopWaveformLoop();
    // Keep the shimmer going through the transcription phase.
    rafRef.current = requestAnimationFrame(drawWaveform);
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }, [drawWaveform, stopWaveformLoop]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      teardownAudio();
      setStatus("idle");
    }
  }, [teardownAudio]);

  if (status === "idle") {
    return (
      <Button
        type="text"
        shape={buttonShape}
        icon={<AudioOutlined />}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => void start()}
        disabled={disabled}
        className={styles.voiceBtn}
        aria-label="Record a voice message"
        title="Record a voice message"
      />
    );
  }

  const transcribing = status === "transcribing";
  const compact = variant === "compact";

  if (compact) {
    return (
      <div
        className={styles.voiceCompactGroup}
        role="group"
        aria-label={transcribing ? "Transcribing audio" : "Recording audio"}
      >
        <div className={styles.voiceCompactWavePill}>
          {transcribing ? (
            <span
              className={classNames(
                styles.voiceTranscribing,
                styles.voiceCompactTranscribing
              )}
            >
              Transcribing
            </span>
          ) : (
            <canvas ref={canvasRef} className={styles.voiceWave} aria-hidden />
          )}
        </div>
        {transcribing ? (
          <Button
            type="primary"
            icon={<LoadingOutlined />}
            disabled
            className={classNames(
              styles.sendBtn,
              styles.stopBtn,
              styles.voiceCompactStop
            )}
            aria-label="Transcribing"
          />
        ) : (
          <Button
            type="primary"
            icon={<span className={styles.voiceCompactStopIcon} aria-hidden />}
            onClick={stop}
            className={classNames(
              styles.sendBtn,
              styles.stopBtn,
              styles.voiceCompactStop
            )}
            aria-label="Stop recording"
            title="Stop"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={styles.voiceOverlay}
      role="group"
      aria-label={transcribing ? "Transcribing audio" : "Recording audio"}
    >
      <Button
        type="text"
        shape="circle"
        icon={<CloseOutlined />}
        onClick={cancel}
        disabled={transcribing}
        className={styles.voiceCancelBtn}
        aria-label="Cancel recording"
        title="Cancel"
      />
      <canvas ref={canvasRef} className={styles.voiceWave} aria-hidden />
      {transcribing ? (
        <span className={styles.voiceTranscribing}>
          <LoadingOutlined /> Transcribing…
        </span>
      ) : null}
      {transcribing ? (
        <Button
          type="primary"
          shape="circle"
          icon={<LoadingOutlined />}
          disabled
          className={classNames(styles.sendBtn, styles.stopBtn)}
          aria-label="Transcribing"
        />
      ) : (
        <Button
          type="primary"
          shape="circle"
          icon={<BorderOutlined />}
          onClick={stop}
          className={classNames(styles.sendBtn, styles.stopBtn)}
          aria-label="Stop recording"
          title="Stop"
        />
      )}
    </div>
  );
};

export default NeoVoiceInput;
